import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface HeaderHintProps {
  label: string;
  definition: string;
}

interface HintPosition {
  top: number;
  left: number;
}

const HINT_WIDTH = 240;
const VIEWPORT_GUTTER = 12;

/**
 * A column heading that explains itself on hover, keyboard focus or tap.
 * The definition renders into the body with fixed positioning, so a
 * scrolling or clipped table container can't cut it off.
 */
export function HeaderHint({ label, definition }: HeaderHintProps) {
  const id = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<HintPosition | null>(null);

  const show = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const box = trigger.getBoundingClientRect();
    const maxLeft = window.innerWidth - HINT_WIDTH - VIEWPORT_GUTTER;
    setPosition({
      top: box.bottom + 6,
      left: Math.max(VIEWPORT_GUTTER, Math.min(box.right - HINT_WIDTH, maxLeft)),
    });
  }, []);
  const hide = useCallback(() => setPosition(null), []);

  // A fixed hint would drift from its heading, so it follows it on scroll
  // (a tap on a phone can scroll the page as it focuses the button).
  const open = position !== null;
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide();
    };
    window.addEventListener('scroll', show, true);
    window.addEventListener('resize', show);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', show, true);
      window.removeEventListener('resize', show);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, show, hide]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="header-hint-trigger"
        aria-describedby={id}
        // Hover is a mouse thing: a tap emits enter and leave around its
        // click, so touch opens on focus and closes on blur instead.
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') show();
        }}
        onPointerLeave={(event) => {
          if (event.pointerType === 'mouse') hide();
        }}
        onFocus={show}
        onBlur={hide}
        // A tap focuses the button first; clicking again shouldn't close it.
        onClick={show}
      >
        {label}
      </button>
      {/* Always in the DOM so aria-describedby resolves; shown only while open. */}
      {createPortal(
        <span
          id={id}
          role="tooltip"
          className="header-hint"
          hidden={!position}
          style={position ? { top: position.top, left: position.left, width: HINT_WIDTH } : {}}
        >
          {definition}
        </span>,
        document.body,
      )}
    </>
  );
}
